import { useEffect, useMemo } from 'react';
import Button from '~/commonComponents/Button';
import { OutlineExternalLinkIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Column } from '~/commonComponents/Column';
import { Pressable } from '~/commonComponents/Pressable';
import { Row } from '~/commonComponents/Row';
import { Typography } from '@tempmee/react';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { useToggle } from '~/hooks';
import { useUserSelector } from '~/hooks/useUserSelector';
import BestInDentalBadge from '~/images/best-in-dental.png';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import { URLUtils } from '~/utils/URLUtils';
import { getBestInDentalStorageKey } from './Constants';

export const BestInDentalNomineePopup = () => {
  const currentUser = useUserSelector();
  const [isOpen, controls] = useToggle(false);

  const isNominee = useMemo(() => {
    return currentUser?.office_info?.bestInDental?.status === 'nominee';
  }, [currentUser?.office_info?.bestInDental?.status]);

  useEffect(() => {
    if (!currentUser) return;

    const key = getBestInDentalStorageKey(currentUser.id);

    const result = localStorage.getItem(key);

    if (result === 'true' || !isNominee) {
      controls.setOff();
      return;
    }

    controls.setOn();
  }, [controls, currentUser, isNominee]);

  const handleDeclineNomination = () => {
    controls.setOff();
    localStorage.setItem(getBestInDentalStorageKey(currentUser.id), 'true');
  };

  const handleSubmitApplication = () => {
    window.open(
      URLUtils.fromTemplate(RemoteConfigUtils.getBestInDentalOfficeNomineeSurvey(), {
        officeId: currentUser?.id,
        officeName: currentUser?.office_name,
      }),
      '_blank',
    );
    controls.setOff();
    localStorage.setItem(getBestInDentalStorageKey(currentUser.id), 'true');
  };

  if (!isOpen) {
    return null;
  }

  return (
    // @ts-expect-error - ConfirmPopup still written in JavaScript
    <ConfirmPopup
      modalStyle={{ maxWidth: 500, maxHeight: 560, paddingTop: 57 }}
      Icon={() => (
        <img alt="Best In Dental Badge" src={BestInDentalBadge} width={164} height={184} />
      )}
      title="Your Office Has Been Nominated!"
      titleStyle={{ marginBlock: 16 }}
      description={
        <Column>
          <Typography as="span" color={colors.neutral[500]} style={{ marginBottom: 12 }}>
            You&apos;ve been handpicked.
          </Typography>
          <Typography as="span" color={colors.neutral[500]}>
            GoTu leadership has nominated your office for our Best in Dental list, an honor reserved
            for the top 10% of practices setting the standard in patient care, team culture, and
            professional reputation. Dental professionals in your area already notice. Now it&apos;s
            your turn to claim it.
          </Typography>
        </Column>
      }
      descriptionStyle={{ marginBottom: 24, width: '85%' }}
    >
      <Pressable onPress={handleSubmitApplication}>
        <Row className="button medium primary" gap={6} width={228}>
          <OutlineExternalLinkIcon color={colors.shades[0]} />
          <Typography as="span" bold color={colors.shades[0]} size="body_large">
            Submit Application
          </Typography>
        </Row>
      </Pressable>
      <Button
        style={{ backgroundColor: colors.secondary[500], width: 256 }}
        text="Decline Nomination"
        onClick={handleDeclineNomination}
      />
    </ConfirmPopup>
  );
};
